import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useModalZIndex } from '../../contexts/ModalStackContext';
import { X, AlertTriangle, CheckCircle } from 'lucide-react';

interface WorkflowCompletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  incompleteSteps: number;
  onCompleteAll: () => Promise<void>;
  onProceed: () => void;
}

export default function WorkflowCompletionModal({
  isOpen,
  onClose,
  incompleteSteps,
  onCompleteAll,
  onProceed
}: WorkflowCompletionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const stackZ = useModalZIndex(9999);

  if (!isOpen) return null;

  const handleCompleteAll = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await onCompleteAll();
      onProceed();
    } catch (err) {
      console.error('Error completing workflow steps:', err);
      setError('Failed to complete workflow steps. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center" style={{ zIndex: stackZ }}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-900">
              Incomplete Workflow Steps
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-gray-700 mb-4">
            There {incompleteSteps === 1 ? 'is' : 'are'} <span className="font-semibold text-gray-900">{incompleteSteps}</span> uncompleted 
            workflow step{incompleteSteps === 1 ? '' : 's'} in this booking.
          </p>
          
          <p className="text-gray-600 text-sm mb-6">
            To mark this booking as completed, all workflow steps must be finished. 
            You can complete all steps now or go back and complete them individually.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleCompleteAll}
              disabled={isLoading}
              className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 
                       transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Completing...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Mark All Steps Completed and Complete Booking</span>
                </>
              )}
            </button>
            
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md 
                       hover:bg-gray-50 transition-colors font-medium
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}